import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import { adminWrapupStatus, adminWrapupPreview } from "@/lib/api";
import { Card } from "./kit";

const fmtWhen = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
};

// Overview card: when the day-7 wrap-up goes out, who's due, and a
// "send me a preview" button that mails the admin their own recap.
export default function WrapupCard() {
    const [data, setData] = useState(null);
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        try { setData(await adminWrapupStatus()); } catch { /* auth gate */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const preview = async () => {
        if (sending) return;
        setSending(true);
        try {
            const r = await adminWrapupPreview();
            if (r.ok) toast.success(`preview on its way to ${r.to} 🍡 check your inbox`);
            else toast.error(r.error || "couldn't send the preview");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "couldn't send the preview");
        } finally { setSending(false); }
    };

    if (!data) return null;
    const run = data.last_run;
    return (
        <Card className="p-6" data-testid="wrapup-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <span className="text-warm-500 text-xs uppercase tracking-wide font-semibold flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" /> Day-{data.wrapup_day} wrap-up email
                    </span>
                    <p className="text-warm-800 text-sm mt-2 max-w-md">
                        On a tester&apos;s 7th day with Mochi (counted from their first active day) they get one warm
                        recap — tasks, habit check-ins, rice balls, focus time, wearables — and a link to the in-app
                        feedback page. Goes out <span className="font-semibold">{data.send_time}</span>.
                    </p>
                    <p className="text-warm-500 text-xs mt-2" data-testid="wrapup-status">
                        <span className="font-semibold text-warm-900" data-testid="wrapup-due-count">{data.due_count}</span> due at the next run
                        {" · "}<span className="font-semibold text-warm-900" data-testid="wrapup-sent-count">{data.sent_count}</span> sent so far
                        {run?.finished_at && <>{" · "}last run {fmtWhen(run.finished_at)} ({run.sent} sent{run.failed ? `, ${run.failed} failed` : ""})</>}
                    </p>
                </div>
                <button
                    onClick={preview}
                    disabled={sending}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-cream bg-terracotta hover:bg-terracotta-hover disabled:opacity-60 transition-colors shrink-0"
                    data-testid="wrapup-preview-btn"
                >
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send me a preview
                </button>
            </div>
        </Card>
    );
}
